/**
 * Форматирование scope/типов сущностей в UI ядра Activity.
 *
 * Все функции диспетчеризуются через реестр дескрипторов по `entity_type`
 * и имеют graceful fallback для незнакомых типов.
 */
import { getActivityEntityDescriptor } from './ActivityEntityRegistry';

type ScopeLike = {
  entity_type?: string | null;
  label?: string | null;
} | null | undefined;

/** Имя типа сущности для UI («Партнер», «Лид», …); fallback — «Сущность». */
export function getActivityEntityTypeLabel(entityType: string | null | undefined): string {
  return getActivityEntityDescriptor(entityType)?.label ?? 'Сущность';
}

/**
 * Текст scope-бейджа сообщения (чип, header reply и т.д.).
 * Без label бейдж не показывается; без дескриптора — просто label.
 */
export function getActivityScopeBadgeText(scope: ScopeLike): string | null {
  const entityType = scope?.entity_type?.trim();
  const scopeLabel = scope?.label?.trim();
  if (!entityType || !scopeLabel) return null;

  const descriptor = getActivityEntityDescriptor(entityType);
  if (!descriptor) return scopeLabel;
  if (descriptor.formatScopeBadge) return descriptor.formatScopeBadge(scopeLabel);
  return `${descriptor.label} ${scopeLabel}`;
}

/** Заголовок scope-контекста в композере; fallback — label или «Контекст». */
export function getActivityScopeTitle(scope: ScopeLike): string {
  const entityType = scope?.entity_type?.trim();
  const scopeLabel = scope?.label?.trim() || null;
  if (!entityType) return scopeLabel ?? 'Контекст';

  const descriptor = getActivityEntityDescriptor(entityType);
  if (!descriptor) return scopeLabel ?? 'Контекст';
  if (descriptor.formatScopeTitle) return descriptor.formatScopeTitle(scopeLabel);
  return scopeLabel ? `${descriptor.label}: ${scopeLabel}` : descriptor.label;
}

/** Inline-маркер подсветки в тексте сообщения; '' — подсветки нет. */
export function getActivityInlineMarker(scope: ScopeLike): string {
  const entityType = scope?.entity_type?.trim();
  if (!entityType) return '';
  const descriptor = getActivityEntityDescriptor(entityType);
  if (!descriptor?.getInlineMarker) return '';
  return descriptor.getInlineMarker(scope?.label?.trim() || null);
}
